/**
 * PlanViewerContext
 *
 * Provides context about the current viewer's access level and role bindings
 * for persona-based plan visibility.
 *
 * ADMIN: Full plan visibility (owner or collaborator)
 * ROLE_MEMBER: Limited visibility (only sees assigned steps)
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';

export type ParticipantAccessLevel = 'ADMIN' | 'ROLE_MEMBER';

export interface PlanViewerContextValue {
  /** The viewer's person ID */
  viewerPersonId: string | null;
  /** The viewer's access level */
  accessLevel: ParticipantAccessLevel;
  /** Role IDs the viewer is bound to */
  roleIds: string[];
  /** Whether the viewer is the plan owner */
  isOwner: boolean;
  /** Check if a step is visible to the viewer */
  isStepVisible: (step: {
    assignedToId?: string | null;
    assignedRoleId?: string | null;
  }) => boolean;
}

const defaultContext: PlanViewerContextValue = {
  viewerPersonId: null,
  accessLevel: 'ADMIN',
  roleIds: [],
  isOwner: true,
  isStepVisible: () => true,
};

const PlanViewerContext = createContext<PlanViewerContextValue>(defaultContext);

export interface PlanViewerProviderProps {
  children: ReactNode;
  viewerPersonId: string | null;
  accessLevel: ParticipantAccessLevel;
  roleIds: string[];
  isOwner: boolean;
}

/**
 * Provider for plan viewer context.
 * Wrap plan pages with this to enable persona-based visibility filtering.
 */
export function PlanViewerProvider({
  children,
  viewerPersonId,
  accessLevel,
  roleIds,
  isOwner,
}: PlanViewerProviderProps) {
  const isStepVisible = useMemo(() => {
    return (step: {
      assignedToId?: string | null;
      assignedRoleId?: string | null;
    }) => {
      // Owner always sees everything
      if (isOwner) {
        return true;
      }

      // ADMIN access level sees everything
      if (accessLevel === 'ADMIN') {
        return true;
      }

      // ROLE_MEMBER: check direct assignment
      if (step.assignedToId && step.assignedToId === viewerPersonId) {
        return true;
      }

      // ROLE_MEMBER: check role-based assignment
      if (step.assignedRoleId && roleIds.includes(step.assignedRoleId)) {
        return true;
      }

      // Not visible
      return false;
    };
  }, [isOwner, accessLevel, viewerPersonId, roleIds]);

  const value = useMemo(
    () => ({
      viewerPersonId,
      accessLevel,
      roleIds,
      isOwner,
      isStepVisible,
    }),
    [viewerPersonId, accessLevel, roleIds, isOwner, isStepVisible]
  );

  return (
    <PlanViewerContext.Provider value={value}>
      {children}
    </PlanViewerContext.Provider>
  );
}

/**
 * Hook to access plan viewer context.
 * Must be used within a PlanViewerProvider.
 */
export function usePlanViewer(): PlanViewerContextValue {
  return useContext(PlanViewerContext);
}

export default PlanViewerContext;
